import { useTraduccion } from '@tallerya/i18n';
import { AvisoError, Boton, Buscador, Chip, Filtro, IconoMas, Listado, useBusquedaDiferida, type Columna, type ElementoMenu } from '@tallerya/ui';
import { useState } from 'react';
import { useNavigate } from 'react-router';
import { useTituloPantalla } from '../../layout/titulo';
import { useAccionDeFila } from '../../componentes/useAccionDeFila';
import { AsignarPersonal } from './AsignarPersonal';
import { EliminarSucursal } from './EliminarSucursal';
import { useCambiosSucursal, useSucursales, type FiltrosSucursales, type Sucursal } from './datos';

// El listado de sucursales (M-1): «Sucursales - Listado» del Figma y sus
// variantes (Menú de fila), (Sin sucursales), (Asignar personal) y (Eliminar).
//
// **Es la pantalla de muestra.** Lo que hace cada maestra está acá en el orden en
// que conviene leerlo:
//
//   1. los filtros viven en el estado de la pantalla, y la búsqueda espera a que
//      la persona deje de escribir antes de preguntar;
//   2. las columnas se declaran, la tabla la dibuja `Listado`;
//   3. el menú de cada fila ofrece solo lo que se puede hacer con ESA fila;
//   4. las acciones del menú corren con `useAccionDeFila`: lo que sale bien se
//      avisa abajo, lo que sale mal se dice arriba del listado con el texto que
//      corresponde al código de la API, y sin red, que no hubo respuesta.
//
// Alta y edición van en su propia página (`FormularioSucursal`), como en el
// Figma. Asignar personal y eliminar, en una ventana sobre el listado.

type VentanaAbierta = { tipo: 'asignar' | 'eliminar'; sucursal: Sucursal } | null;

export function ListadoSucursales() {
  const { t } = useTraduccion();
  const navegar = useNavigate();
  const cambios = useCambiosSucursal();
  useTituloPantalla(t('admin.sucursales.titulo'));

  const [texto, setTexto] = useState('');
  const [filtros, setFiltros] = useState<FiltrosSucursales>({ buscar: '', estado: 'todas', cursor: null });
  // Por dónde se pasó: la API solo sabe ir hacia adelante, así que volver es
  // sacar el último cursor de esta pila.
  const [anteriores, setAnteriores] = useState<(string | null)[]>([]);
  const [ventana, setVentana] = useState<VentanaAbierta>(null);
  const { hacer, problema } = useAccionDeFila();

  // Un filtro nuevo vuelve a la primera página: el cursor de la búsqueda anterior
  // no significa nada en la nueva.
  function filtrar(cambio: Partial<Omit<FiltrosSucursales, 'cursor'>>) {
    setFiltros((f) => ({ ...f, ...cambio, cursor: null }));
    setAnteriores([]);
  }

  useBusquedaDiferida(
    texto,
    (buscar) => {
      setFiltros((f) => ({ ...f, buscar, cursor: null }));
      setAnteriores([]);
    },
    { pendiente: texto !== filtros.buscar },
  );

  const consulta = useSucursales(filtros);
  const pagina = consulta.data;

  function opciones(s: Sucursal): ElementoMenu[] {
    const id = String(s.id);
    const nombre = { nombre: s.name };

    // Solo lo que se puede: la principal no se apaga ni se elimina, y una fuera
    // de servicio no puede ser principal. Ofrecerlo para después decir que no es
    // hacerle perder un clic a quien está trabajando.
    return [
      { etiqueta: t('admin.sucursales.menu.editar'), onSeleccionar: () => void navegar(`/sucursales/${id}`) },
      {
        etiqueta: t('admin.sucursales.menu.asignarPersonal'),
        onSeleccionar: () => setVentana({ tipo: 'asignar', sucursal: s }),
      },
      ...(!s.isMain && s.isActive
        ? [
            {
              etiqueta: t('admin.sucursales.menu.nombrarPrincipal'),
              onSeleccionar: () =>
                void hacer(cambios.nombrarPrincipal(id), t('admin.sucursales.aviso.principal', nombre)),
            },
          ]
        : []),
      ...(s.isMain
        ? []
        : [
            s.isActive
              ? {
                  etiqueta: t('admin.sucursales.menu.fueraDeServicio'),
                  onSeleccionar: () =>
                    void hacer(cambios.cambiarEstado(id, false), t('admin.sucursales.aviso.fueraDeServicio', nombre)),
                }
              : {
                  etiqueta: t('admin.sucursales.menu.enServicio'),
                  onSeleccionar: () =>
                    void hacer(cambios.cambiarEstado(id, true), t('admin.sucursales.aviso.enServicio', nombre)),
                },
            { tipo: 'divisor' as const },
            {
              etiqueta: t('admin.sucursales.menu.eliminar'),
              peligro: true,
              onSeleccionar: () => setVentana({ tipo: 'eliminar', sucursal: s }),
            },
          ]),
    ];
  }

  const columnas: Columna<Sucursal>[] = [
    { id: 'codigo', titulo: t('admin.sucursales.columna.codigo'), ancho: 110, celda: (s) => s.code },
    {
      id: 'nombre',
      titulo: t('admin.sucursales.columna.nombre'),
      ancho: 190,
      celda: (s) => (
        <span className="celda-nombre">
          {s.name}
          {s.isMain && <Chip tono="marca">{t('admin.sucursales.principal')}</Chip>}
        </span>
      ),
      tarjeta: 'titulo',
    },
    {
      id: 'direccion',
      titulo: t('admin.sucursales.columna.direccion'),
      celda: (s) => s.address ?? '—',
      tarjeta: 'renglon',
    },
    {
      id: 'telefono',
      titulo: t('admin.sucursales.columna.telefono'),
      ancho: 110,
      celda: (s) => s.phone ?? '—',
      tarjeta: 'pie',
    },
    {
      id: 'personal',
      titulo: t('admin.sucursales.columna.personal'),
      ancho: 90,
      alinear: 'centro',
      celda: (s) => s.staffCount,
      // Un número suelto en el pie no dice de qué es: «Personal 4».
      tarjeta: { lugar: 'pie', conTitulo: true },
    },
    {
      id: 'estado',
      titulo: t('admin.sucursales.columna.estado'),
      ancho: 150,
      celda: (s) =>
        s.isActive ? (
          <Chip tono="exito">{t('admin.sucursales.enServicio')}</Chip>
        ) : (
          <Chip>{t('admin.sucursales.fueraDeServicio')}</Chip>
        ),
      tarjeta: 'esquina',
    },
  ];

  const botonNueva = (
    <Boton icono={<IconoMas />} onClick={() => void navegar('/sucursales/nueva')}>
      {t('admin.sucursales.nueva')}
    </Boton>
  );

  return (
    <>
      {problema && <AvisoError aviso={{ titulo: t('admin.sucursales.error.titulo'), texto: problema }} />}

      <Listado
        titulo={t('admin.sucursales.titulo')}
        info={t('admin.sucursales.info')}
        columnas={columnas}
        filas={pagina?.items}
        claveFila={(s) => String(s.id)}
        estado={consulta.isPending ? 'cargando' : consulta.isError ? 'error' : 'listo'}
        onReintentar={() => void consulta.refetch()}
        filtros={
          <>
            <Buscador valor={texto} onCambiar={setTexto} placeholder={t('admin.sucursales.buscar')} />
            <Filtro
              etiqueta={t('admin.sucursales.filtro.estado')}
              valor={filtros.estado}
              onCambiar={(valor) => filtrar({ estado: valor as FiltrosSucursales['estado'] })}
              opciones={[
                { valor: 'todas', etiqueta: t('admin.sucursales.filtro.todas') },
                { valor: 'activas', etiqueta: t('admin.sucursales.enServicio') },
                { valor: 'inactivas', etiqueta: t('admin.sucursales.fueraDeServicio') },
              ]}
            />
          </>
        }
        acciones={botonNueva}
        // Una página que no es la primera cuenta como filtro: si queda vacía —se
        // eliminó su última fila—, el taller no está vacío, y decirle «Aún no hay
        // sucursales» sería falso. Así sale «sin resultados», que vuelve al inicio.
        hayFiltros={filtros.buscar !== '' || filtros.estado !== 'todas' || filtros.cursor !== null}
        onLimpiarFiltros={() => {
          setTexto('');
          filtrar({ buscar: '', estado: 'todas' });
        }}
        vacio={{
          titulo: t('admin.sucursales.vacio.titulo'),
          texto: t('admin.sucursales.vacio.texto'),
          acciones: botonNueva,
        }}
        {...(filtros.buscar && {
          detalleSinResultados: t('admin.sucursales.sinResultados', { texto: filtros.buscar }),
        })}
        opcionesFila={opciones}
        paginacion={{
          hayAnterior: anteriores.length > 0,
          hayMas: pagina?.hasMore ?? false,
          onAnterior: () => {
            setFiltros((f) => ({ ...f, cursor: anteriores.at(-1) ?? null }));
            setAnteriores((a) => a.slice(0, -1));
          },
          onSiguiente: () => {
            if (!pagina?.nextCursor) return;
            setAnteriores((a) => [...a, filtros.cursor]);
            setFiltros((f) => ({ ...f, cursor: pagina.nextCursor }));
          },
        }}
      />

      <AsignarPersonal
        sucursal={ventana?.tipo === 'asignar' ? ventana.sucursal : null}
        onCerrar={() => setVentana(null)}
      />
      <EliminarSucursal
        sucursal={ventana?.tipo === 'eliminar' ? ventana.sucursal : null}
        onCerrar={() => setVentana(null)}
      />
    </>
  );
}
